import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { GlobalSearch } from '@/components/shared/GlobalSearch';
import { useBranding } from '@/lib/branding';
import { DemoModeProvider, DemoModeTrigger as DemoModeTriggerButton } from '@/components/demo/DemoMode';
import {
  LayoutDashboard, Users, UserCircle, DollarSign, Briefcase,
  Calendar, ClipboardCheck, FileText, LogOut, Menu, Clock,
  MapPin, ChevronRight, ChevronDown, ChevronLeft, Shield, Settings,
  Building2, User, Bell, AlertTriangle, ScrollText, Home, Wrench,
  Banknote, BarChart3, Sparkles, TrendingUp, Lightbulb, Bookmark,
  MessageSquare, Mail, Link2, Globe, Webhook, Search, Plus,
  Heart, Pill, Activity, Zap, Eye,
  PanelLeftClose, PanelLeftOpen, Package,
} from 'lucide-react';
import {
  sidebarConfig,
  sidebarConfigV2,
  staffSidebarConfig,
  QUICK_ACTIONS,
} from '@/config/sidebarConfig';


// ════════════════════════════════════════════════════════════════
// SIDEBAR COMPONENTS
// ════════════════════════════════════════════════════════════════

function SidebarGroup({ group, collapsed, hasPermission, location, onClose, badges, userRole, featureFlags }) {
  const [open, setOpen] = useState(!group.collapsedByDefault);
  const Icon = group.icon;

  // Group-level role restriction (e.g., System Admin group)
  if (group.requireRole && userRole !== group.requireRole) return null;

  const visibleItems = group.items.filter(item => {
    // Check role restriction first
    if (item.requireRole && userRole !== item.requireRole) return false;
    // Check feature flag gating
    if (item.featureFlag && !featureFlags[item.featureFlag]) return false;
    // Then check permissions
    return item.permissions.length === 0 || item.permissions.some(p => hasPermission(p));
  });

  if (visibleItems.length === 0) return null;

  const isGroupActive = visibleItems.some(item => location.pathname === item.path);

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={visibleItems[0].path}
              className={`flex items-center justify-center h-10 w-10 mx-auto rounded-lg transition-colors ${isGroupActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              onClick={onClose}
            >
              <Icon className="h-5 w-5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">{group.title}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        data-testid={`nav-group-${group.title.toLowerCase().replace(/[&\s]+/g, '-')}`}
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-left">{group.title}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="space-y-0.5 ml-2">
          {visibleItems.map(item => {
            const isActive = location.pathname === item.path;
            const badgeCount = item.badge ? badges[item.badge] : 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                data-testid={`nav-${item.name.toLowerCase().replace(/[\s/]+/g, '-')}`}
                className={`flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors ${isActive ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              >
                <span className="flex-1">{item.name}</span>
                {badgeCount > 0 && <Badge variant="destructive" className="h-5 min-w-[20px] text-[10px] px-1.5">{badgeCount > 99 ? '99+' : badgeCount}</Badge>}
                {isActive && <ChevronRight className="h-3 w-3 opacity-50" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ onClose, collapsed, onToggleCollapse }) {
  const { user, isAdmin, hasPermission, logout } = useAuth();
  const branding = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showQuick, setShowQuick] = useState(false);
  const [badges, setBadges] = useState({});
  const [featureFlags, setFeatureFlags] = useState({});

  const isSystemAdmin = user?.role_name === 'Super Admin';
  const config = isAdmin ? (featureFlags.sidebar_v2_structure_enabled ? sidebarConfigV2 : sidebarConfig) : staffSidebarConfig;

  // Load badge counts + feature flags
  useEffect(() => {
    if (!isAdmin) return;
    const loadBadges = async () => {
      try {
        const [alertsRes] = await Promise.all([
          api.get('/org/notifications/my').catch(() => ({ data: { unread_count: 0 } })),
        ]);
        setBadges({ alerts: alertsRes.data.unread_count || 0 });
      } catch {}
    };
    const loadFlags = async () => {
      try {
        const res = await api.get('/org/settings');
        const flags = res.data?.data?.feature_flags || res.data?.feature_flags || {};
        setFeatureFlags(flags);
      } catch {}
    };
    loadBadges();
    loadFlags();
    const interval = setInterval(loadBadges, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Filter by search
  const filteredConfig = useMemo(() => {
    if (!search.trim()) return config;
    const q = search.toLowerCase();
    return config.map(group => ({
      ...group,
      items: group.items.filter(item => item.name.toLowerCase().includes(q) || group.title.toLowerCase().includes(q)),
    })).filter(group => group.items.length > 0);
  }, [config, search]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex flex-col h-full bg-card">
      {/* Header */}
      <div className={`flex items-center ${collapsed ? 'justify-center p-3' : 'justify-between p-4'}`}>
        {collapsed ? (
          branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.organisationName} className="h-8 w-8 rounded-lg object-contain" onError={e => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<div class="h-8 w-8 rounded-lg bg-primary flex items-center justify-center"><svg class="h-4 w-4 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>'; }} />
          ) : (
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Shield className="h-4 w-4 text-primary-foreground" />
            </div>
          )
        ) : (
          <>
            <div className="flex items-center gap-2.5">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={branding.organisationName} className="h-8 w-8 rounded-lg object-contain" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
              ) : (
                <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                  <Shield className="h-4 w-4 text-primary-foreground" />
                </div>
              )}
              <div>
                <h1 className="text-base font-bold tracking-tight">{branding.organisationName}</h1>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">NDIS Provider</p>
              </div>
            </div>
            {onToggleCollapse && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onToggleCollapse} data-testid="collapse-sidebar">
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
      </div>

      {!collapsed && (
        <>
          {/* Search */}
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-8 pl-8 text-xs bg-muted/50 border-0"
                data-testid="sidebar-search"
              />
            </div>
          </div>

          {/* Quick Create */}
          {isAdmin && (
            <div className="px-3 pb-2">
              <DropdownMenu open={showQuick} onOpenChange={setShowQuick}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full h-8 text-xs gap-1.5" data-testid="quick-create-btn">
                    <Plus className="h-3.5 w-3.5" />Quick Create
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {QUICK_ACTIONS.map(a => (
                    <DropdownMenuItem key={a.name} onClick={() => { navigate(a.path); setShowQuick(false); onClose?.(); }}>
                      <a.icon className="h-4 w-4 mr-2" />{a.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Demo Mode Trigger — System Admin only */}
          {isSystemAdmin && featureFlags.sales_demo_mode_enabled && (
            <div className="px-3 pb-2">
              <DemoModeTriggerButton />
            </div>
          )}
        </>
      )}

      <Separator />

      {/* Nav Groups */}
      <nav className="flex-1 overflow-y-auto py-2 px-1.5" data-testid="sidebar-nav">
        {filteredConfig.map(group => (
          <SidebarGroup
            key={group.title}
            group={group}
            collapsed={collapsed}
            hasPermission={hasPermission}
            location={location}
            onClose={onClose}
            badges={badges}
            userRole={user?.role_name}
            featureFlags={featureFlags}
          />
        ))}
      </nav>

      <Separator />

      {/* User */}
      <div className={collapsed ? 'p-2' : 'p-3'}>
        {collapsed ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleLogout} className="h-9 w-9 mx-auto rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary hover:bg-destructive/10 hover:text-destructive transition-colors">
                  {user?.full_name?.charAt(0) || '?'}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{user?.full_name} — Sign out</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">
                {user?.full_name?.charAt(0) || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-[10px] text-muted-foreground truncate">{user?.role_name}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start text-xs text-muted-foreground hover:text-destructive h-7" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-3.5 w-3.5 mr-2" />Sign out
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// APP SHELL
// ════════════════════════════════════════════════════════════════

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await api.get('/org/branches');
        setBranches(res.data.data || []);
        const assigned = user?.assigned_branch_ids || [];
        if (assigned.length === 1) setSelectedBranch(assigned[0]);
      } catch (e) {}
    };
    if (user) fetchBranches();
  }, [user]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await api.get('/org/notifications/my');
        setNotifications(res.data.data || []);
        setUnreadCount(res.data.unread_count || 0);
      } catch (e) {}
    };
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userBranches = user?.assigned_branch_ids?.length > 0
    ? branches.filter(b => user.assigned_branch_ids.includes(b.code))
    : branches;
  const showBranchSelector = userBranches.length > 1 || (user?.scope_mode === 'ALL' && branches.length > 1);

  return (
    <DemoModeProvider>
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex lg:flex-col border-r transition-all duration-200 ${collapsed ? 'lg:w-[60px]' : 'lg:w-[260px]'}`}>
        <SidebarContent onClose={() => {}} collapsed={collapsed} onToggleCollapse={() => setCollapsed(!collapsed)} />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[280px] p-0">
          <SidebarContent onClose={() => setMobileOpen(false)} collapsed={false} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 sticky top-0 z-40 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b flex items-center px-4 lg:px-6 gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="h-5 w-5" />
          </Button>

          {/* Expand button when collapsed */}
          {collapsed && (
            <Button variant="ghost" size="icon" className="hidden lg:flex h-8 w-8" onClick={() => setCollapsed(false)} data-testid="expand-sidebar">
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          )}

          {showBranchSelector && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="branch-selector">
                  <SelectValue placeholder="All branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {(user?.scope_mode === 'ALL' ? branches : userBranches).map(b => (
                    <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex-1" />

          {/* Global Search */}
          <GlobalSearch />

          {/* Notification Bell */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground flex items-center justify-center px-1">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={async () => {
                    try { await api.post('/org/notifications/read-all'); setUnreadCount(0); setNotifications(prev => prev.map(n => ({...n, is_read: true}))); } catch {}
                  }} data-testid="mark-all-read">Mark all read</Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifications.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No notifications</div>
              ) : notifications.slice(0, 10).map(n => (
                <DropdownMenuItem key={n.id} className={`flex flex-col items-start gap-1 py-2 ${!n.is_read ? 'bg-accent/30' : ''}`}>
                  <p className={`text-sm ${!n.is_read ? 'font-medium' : ''}`}>{n.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 h-9 px-3" data-testid="user-menu-trigger">
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                  {user?.full_name?.charAt(0) || '?'}
                </div>
                <span className="hidden md:inline text-sm font-medium">{user?.full_name}</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="text-sm font-medium">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{user?.role_name} · {user?.scope_mode}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="user-menu-logout">
                <LogOut className="h-4 w-4 mr-2" />Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="px-4 md:px-6 lg:px-8 py-6 max-w-[1400px]">{children}</div>
        </main>
      </div>
    </div>
    </DemoModeProvider>
  );
}
